<template>
  <div class="table-responsive">
    <b-table
      show-empty
      class="sticky-head-table table-wrapper"
      :fields="computedFields"
      :items="items"
      :foot-clone="isBusy && items.length > 0"
    >
      <template #empty>
        <div v-show="!isBusy" class="text-center my-4">
          {{ $t('global.text.noData') }}
        </div>
      </template>

      <template #cell(is_failed)="data">
        <div :class="`status ${!data.value ? 'status--success' : 'status--failed'}`">
          <i v-if="!data.value" class="mc-icon icon-fill_checkmark"></i>
          <i v-else class="mc-icon icon-outline_close"></i>
        </div>
      </template>

      <template #cell()="{ value, field, item }">
        <div class="d-flex flex-column detail-row">
          <div class="detail-row__value">
            {{ value || '-' }}
          </div>
          <div class="detail-row__error">
            {{ item[field.showErrorKey]?.error || '' }}
          </div>
        </div>
      </template>

      <template #head()="data">
        <div class="d-flex gap-3 justify-content-between" @click="handleSort(data)">
          <span class="label">
            {{ data.label }}
          </span>
          <div v-if="data.field.isSortable" class="position-relative">
            <i
              v-if="showSortingIcon(data.field.key, 'asc')"
              class="sort-icon-up__import mc-icon icon-outline_chevron-up"
            ></i>
            <i
              v-if="showSortingIcon(data.field.key, 'desc')"
              class="sort-icon-down__import mc-icon icon-outline_chevron-down"
            ></i>
          </div>
        </div>
      </template>

      <!-- For showing skeleton when fetching next page data, only rendered when items.length > 0, (see foot-clone attr) -->
      <template #foot()>
        <b-skeleton />
      </template>
    </b-table>

    <!-- Observe visibility to get next page data -->
    <div
      v-if="items.length > 0"
      v-observe-visibility="handleElementObserved"
      style="height: 2px"
    ></div>
  </div>
</template>

<script>
import { formatIndexDate } from '@/helpers/tzDate'

export default {
  props: {
    fields: {
      type: Array,
      required: true,
    },
    listGetter: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      sorts: {},
      totalCount: 0,
      page: 1,
      limit: 10,
      isBusy: false,
      isGetDataPending: false,
    }
  },
  computed: {
    hasNextPage() {
      return this.page * this.limit < this.totalCount
    },
    computedFields() {
      return [
        {
          key: 'is_failed',
          label: '',
          thAttr: {
            style: 'min-width:40px;width:40px;',
          },
        },
        ...this.fields.map((field) => ({
          ...field,
          label: this.$t(field.label),
        })),
      ]
    },
  },
  watch: {
    sorts: {
      deep: true,
      handler(val) {
        this.page = 1
        this.getData({ limit: this.limit, page: 1, sorts: this.formatSorts(val) }, true)
      },
    },
  },
  mounted() {
    this.getData({
      limit: this.limit,
      page: 1,
      sorts: this.formatSorts(this.sorts),
    })
  },
  methods: {
    formatIndexDate,
    handleSort(data) {
      if (this.isGetDataPending) return
      let { key, isSortable } = data.field

      if (key === 'number') {
        key = `${key}.value`
      }

      if (!isSortable) return

      if (!(key in this.sorts)) {
        this.$set(this.sorts, key, 'asc')
        return
      }

      if (this.sorts[key] === 'asc') {
        this.$set(this.sorts, key, 'desc')
        return
      }

      this.$delete(this.sorts, key)
    },
    showSortingIcon(key, sortDirection) {
      if (key === 'number') {
        key = `${key}.value`
      }

      if (!(key in this.sorts)) {
        return true
      }

      if (this.sorts[key] === sortDirection) {
        return true
      }

      return false
    },
    async getData(opts, reset = false) {
      this.isBusy = true
      this.isGetDataPending = true

      if (reset) {
        this.items = []
      }

      const res = await this.listGetter(opts)

      if (!res) {
        this.isGetDataPending = false
        return
      }

      this.items = [...this.items, ...res.data.paginated_result]
      this.totalCount = res.metadata.total_count
      this.isBusy = false
      this.isGetDataPending = false
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return

      this.getData({
        limit: this.limit,
        page: ++this.page,
        sorts: this.formatSorts(this.sorts),
      })
    },
    formatSorts(sorts) {
      return Object.entries(sorts).reduce((prev, [key, value]) => [...prev, `${key} ${value}`], [])
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(table) {
  td {
    position: relative;
    vertical-align: middle;
  }

  .label {
    white-space: nowrap;
    color: black;
  }

  .sort-icon-up__import {
    position: absolute;
    right: 10px;
    top: 0px;
    opacity: 0.7;
  }

  .sort-icon-down__import {
    position: absolute;
    right: 10px;
    bottom: 0px;
    opacity: 0.7;
  }

  .status {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: grid;
    place-items: center;

    &--success {
      color: $green;
      background-color: $green-100;
    }

    &--failed {
      color: $red;
      background-color: #fee8e5;
    }
  }

  .detail-row {
    &__error {
      font-size: 10px;
      color: $red-400;
    }
  }
}
</style>
